import React, {useState, useEffect} from 'react';
import {useAuth0} from '@auth0/auth0-react';
import {useNavigate} from 'react-router-dom';
import CreationFlowBar from 'src/components/CreationFlowBar';
import LoadingButton from '@mui/lab/LoadingButton';
import {Error as ErrorComponent} from 'src/components/Error';
import {createAnalysis, startAnalysis} from 'src/utils/bff-communication';
import {AssignmentOutlined, CheckCircleOutlineRounded, SettingsOutlined} from '@mui/icons-material';
import MotifAnalysisStep0 from './MotifAnalysisStep0';
import MotifReview from './MotifReview';
import MotifParameters from './MotifParameters';
import {useBff, getAppConfig} from 'src/utils/config';
import {Box, styled, Typography} from '@mui/material';

interface Step {
    name: string;
    caption: string;
    description: string;
    component: React.ReactNode;
    icon: React.ReactNode;
}

const FormContainer = styled(Box)({
    flex: 5,
    display: 'flex',
    flexDirection: 'column',
    width: '990px',
    minWidth: '600px',
    padding: '1.5% 2%',
    gap: 10
});

interface CreateMotifAnalysisProps {
    experiment: any;
}

export function CreateMotifAnalysis({experiment}: CreateMotifAnalysisProps) {
    const {apiHost} = getAppConfig();
    const [ableToSave, setAbleToSave] = useState(false);
    const {getAccessTokenSilently} = useAuth0();
    const [submissionError, setSubmissionError] = useState<string>('');
    const [step, setStep] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();
    const [dbaMotifInputs, dbaMotifInputsError, dbaMotifInputsLoading] = useBff('GET', `experiment/${experiment.id}/dba-motif-inputs`);

    const [formData, setFormData] = useState<MotifFormData>({
        type: 'ChIP Motif',
        name: '',
        description: '',
        differential_binding_analysis_id: '',
        sample_ids: [],
        motif_analysis: {
            ame_method: 'fisher',
            nmotifs: 5,
            e_val: 0.05
        }
    });

    const loadError =
        dbaMotifInputsError.error ||
        (dbaMotifInputsError.status ? `Error fetching DBA motif inputs (status ${dbaMotifInputsError.status}).` : '');
    const error = loadError || submissionError;
    const dbaOptions = (dbaMotifInputs ?? []) as DbaMotifAnalysisOption[];

    // Reset motif parameters and use the samples from the selected DBA.
    useEffect(() => {
        if (!formData.differential_binding_analysis_id || dbaMotifInputs === null) return;

        const selectedAnalysis = (dbaMotifInputs as DbaMotifAnalysisOption[]).find(
            analysis => analysis.analysis_id === formData.differential_binding_analysis_id
        );
        if (!selectedAnalysis) return;

        setFormData(prev => ({
            ...prev,
            sample_ids: selectedAnalysis.sample_ids,
            motif_analysis: {
                ame_method: 'fisher',
                nmotifs: 5,
                e_val: 0.05
            }
        }));
    }, [formData.differential_binding_analysis_id, dbaMotifInputs]);

    const handleContinueClick = async (stepIndex: number) => {
        if (stepIndex < steps.length - 1) {
            setStep(stepIndex + 1);
        } else {
            setIsSubmitting(true);
            setSubmissionError('');
            try {
                const accessToken = await getAccessTokenSilently();
                const analysis = await createAnalysis(experiment, formData, apiHost, accessToken);
                await startAnalysis(experiment.id, analysis.id, formData, apiHost, accessToken);
                navigate(`/experiment/${experiment.id}`);
            } catch (err) {
                console.error('Error creating analysis:', err);
                setSubmissionError(err instanceof Error ? err.message : 'Unknown error');
                setIsSubmitting(false);
            }
        }
    };

    const steps: Step[] = [
        {
            name: 'Analysis details',
            caption: 'Name your analysis, choose differential binding analysis, and provide a description',
            description: 'Name your analysis, choose differential binding analysis, and optionally provide a description.',
            component: (
                <MotifAnalysisStep0
                    formData={formData}
                    setFormData={setFormData}
                    handleNext={setAbleToSave}
                    error={loadError}
                    differentialBindingAnalysisList={dbaOptions}
                    isLoading={dbaMotifInputsLoading}
                />
            ),
            icon: <AssignmentOutlined sx={{fill: step === 0 ? 'url(#linearColors)' : ''}} />
        },
        {
            name: 'Edit analysis parameter settings',
            caption: 'Advanced configuration parameters for analysis.',
            description: 'Customize options for data analysis or use our default configuration.',
            component: <MotifParameters formData={formData} setFormData={setFormData} setValidForm={setAbleToSave} />,
            icon: <SettingsOutlined sx={{fill: step === 1 ? 'url(#linearColors)' : ''}} />
        },
        {
            name: 'Review analysis',
            caption: 'Check your setup before submitting for analysis.',
            description:
                'Confirm your analysis setup. Once an analysis has been started, its settings cannot be changed. If you want to run variations of this analysis, you will need to go through the setup process again.',
            component: <MotifReview formData={formData} handleNext={setStep} dbaAnalyses={dbaOptions} />,
            icon: <CheckCircleOutlineRounded sx={{fill: step === 2 ? 'url(#linearColors)' : ''}} />
        }
    ];

    return (
        <>
            <CreationFlowBar title="SETUP NEW ANALYSIS" steps={steps} step={step} setStep={setStep} />
            <FormContainer>
                <Typography variant="headline" size="large">
                    {steps[step].name}
                </Typography>
                <Typography variant="body" size="medium" mb={2}>
                    {steps[step].description}
                </Typography>
                {steps[step].component}
                {error && <ErrorComponent message={error} />}
                <LoadingButton
                    variant="contained"
                    color="primary"
                    onClick={() => handleContinueClick(step)}
                    disabled={!ableToSave}
                    loading={isSubmitting}>
                    {step !== 2 ? 'Continue' : 'Run analysis'}
                </LoadingButton>
            </FormContainer>
        </>
    );
}
